import { __spreadArrays } from "tslib";
import moment from 'moment-timezone';
import { evaluateCondition, UnavailabilityReason, UnavailabileVariationReason, } from '../index';
import { evaluateHierarchyConditions } from './evaluateHierarchyConditions';
import { extractLocalizedString } from './i18nLogic';
import { slugCreatorFactory } from './slugLogic';
import { calcMenuItemPriceRange } from './priceRangeLogic';
import { getErrorsOfChoice, } from './displayableOrderItem';
export function getItemsHash(menu) {
    var result = {};
    menu.items.forEach(function (item) {
        result[item.id] = item;
    });
    return result;
}
export function extractImageUrl(item) {
    var _a;
    if ((_a = item.media) === null || _a === void 0 ? void 0 : _a.logo) {
        return item.media.logo.replace('https://static.wixstatic.com/media/', '');
    }
    return undefined;
}
export function getDisplayableMenu(menu, locale, currency, dispatchTime, platform, dispatchType, mode) {
    if (mode === void 0) { mode = 'online-ordering'; }
    var itemsHash = getItemsHash(menu);
    var slugCreator = slugCreatorFactory();
    var result = [];
    var conditionParams = { dispatchTime: dispatchTime, platform: platform, dispatchType: dispatchType };
    var isAvailable = function (conditionResult) {
        return !conditionResult.reasons.find(function (reason) { return reason.type === 'order_delivery_time'; });
    };
    var isHidden = function (conditionResult) {
        return !conditionResult.result &&
            conditionResult.reasons.find(function (reason) { return reason.type === 'false'; });
    };
    var isIncorrectDispatchType = function (conditionResult, _mode) {
        return !conditionResult.result &&
            !!conditionResult.reasons.find(function (reason) { return reason.type === 'order_delivery_type' && _mode !== 'catalog'; });
    };
    var isIncorrectPlatformType = function (conditionResult, _mode) {
        return !conditionResult.result &&
            !!conditionResult.reasons.find(function (reason) { return reason.type === 'order_platform' && _mode !== 'catalog'; });
    };
    menu.sections.forEach(function (section) {
        var subMenuConditionResult = evaluateCondition(section.displayCondition, conditionParams);
        if (isHidden(subMenuConditionResult) ||
            isIncorrectDispatchType(subMenuConditionResult, mode) ||
            section.id === 'trash' ||
            isIncorrectPlatformType(subMenuConditionResult, mode)) {
            return;
        }
        var subMenuSlug = slugCreator(extractLocalizedString(section.title, locale));
        var subMenu = {
            id: section.id,
            title: section.title,
            displayableTitle: extractLocalizedString(section.title, locale),
            description: section.description,
            displayableDescription: extractLocalizedString(section.description, locale),
            sections: [],
            available: isAvailable(subMenuConditionResult),
            slug: subMenuSlug,
        };
        (section.children || []).forEach(function (menuSection) {
            var _a;
            var sectionConditionResult = evaluateHierarchyConditions({
                entity: menuSection,
                menuRoot: menu,
                conditionParams: conditionParams,
                itemsHash: itemsHash,
            });
            if (isHidden(sectionConditionResult) ||
                isIncorrectDispatchType(sectionConditionResult, mode) ||
                isIncorrectPlatformType(sectionConditionResult, mode)) {
                return;
            }
            if ((_a = menuSection.itemIds) === null || _a === void 0 ? void 0 : _a.length) {
                var sectionsSlug = slugCreator(extractLocalizedString(menuSection.title, locale));
                var displayableSection_1 = {
                    id: menuSection.id,
                    title: menuSection.title,
                    displayableTitle: extractLocalizedString(menuSection.title, locale),
                    description: menuSection.description,
                    displayableDescription: extractLocalizedString(menuSection.description, locale),
                    items: [],
                    available: isAvailable(sectionConditionResult),
                    image: extractImageUrl(menuSection),
                    slug: subMenuSlug + "/" + sectionsSlug,
                };
                menuSection.itemIds.forEach(function (itemId) {
                    var menuItem = itemsHash[itemId];
                    var id = menuItem.id, title = menuItem.title, description = menuItem.description, price = menuItem.price, labels = menuItem.labels, variations = menuItem.variations;
                    var itemConditionResult = evaluateHierarchyConditions({
                        entity: menuItem,
                        menuRoot: menu,
                        conditionParams: conditionParams,
                        sectionHint: menuSection,
                        itemsHash: itemsHash,
                    });
                    if (isHidden(itemConditionResult) ||
                        isIncorrectDispatchType(itemConditionResult, mode) ||
                        isIncorrectPlatformType(itemConditionResult, mode)) {
                        return;
                    }
                    var _a = calcMenuItemPriceRange(menu, menuItem.id), minPrice = _a.min, maxPrice = _a.maxPriceOfMandatoryChoices;
                    var _b = (variations || []).reduce(function (prev, variation) {
                        var _a = hasVariationSelectableChoices({
                            variation: variation,
                            dispatchTime: dispatchTime,
                            platform: platform,
                            dispatchType: dispatchType,
                            menu: menu,
                            itemsHash: itemsHash,
                        }), hasEnoughSelectableChoices = _a.hasEnoughSelectableChoices, reasons = _a.reasons;
                        var isChoiceSoldOut = Boolean(reasons === null || reasons === void 0 ? void 0 : reasons.includes(UnavailabileVariationReason.SoldOut));
                        return {
                            hasSoldoutVariationError: prev.hasSoldoutVariationError || isChoiceSoldOut,
                            hasUnavailableVariationError: prev.hasUnavailableVariationError ||
                                (!hasEnoughSelectableChoices && !isChoiceSoldOut),
                        };
                    }, {
                        hasSoldoutVariationError: false,
                        hasUnavailableVariationError: false,
                    }), hasSoldoutVariationError = _b.hasSoldoutVariationError, hasUnavailableVariationError = _b.hasUnavailableVariationError;
                    displayableSection_1.items.push({
                        id: id,
                        title: title,
                        displayableTitle: extractLocalizedString(title, locale),
                        description: description,
                        displayableDescription: extractLocalizedString(description, locale),
                        minPrice: minPrice,
                        price: price,
                        maxPrice: maxPrice,
                        displayableMinPrice: getDisplayablePrice(minPrice, locale, currency),
                        displayablePrice: price
                            ? getDisplayablePrice(price, locale, currency)
                            : '',
                        displayableMaxPrice: getDisplayablePrice(maxPrice, locale, currency),
                        image: extractImageUrl(menuItem),
                        labels: labels || [],
                        available: isAvailable(itemConditionResult) && !hasUnavailableVariationError,
                        soldout: isSoldOut(menuItem, dispatchTime) || hasSoldoutVariationError,
                        currency: currency,
                    });
                });
                if (displayableSection_1.items.length) {
                    subMenu.sections.push(displayableSection_1);
                }
            }
        });
        result.push(subMenu);
    });
    return __spreadArrays(result.filter(function (section) { return section.available; }), result.filter(function (section) { return !section.available; }));
}
export function getCompactMenus(menu, locale, params) {
    if (params === void 0) { params = {
        platform: 'web',
        dispatchType: 'premises',
        dispatchTime: moment(),
    }; }
    return menu.sections
        .filter(function (section) { return section.id !== 'trash'; })
        .map(function (section) {
        var _a;
        var imageItem = (_a = section.children) === null || _a === void 0 ? void 0 : _a.find(function (item) { var _a; return (_a = item.media) === null || _a === void 0 ? void 0 : _a.logo; });
        var conditionResult = evaluateCondition(section.displayCondition, params);
        return {
            id: section.id,
            title: extractLocalizedString(section.title, locale),
            sections: (section.children || []).map(function (menuSection) {
                var sectionConditionResult = evaluateCondition(menuSection.displayCondition, params);
                return {
                    id: menuSection.id,
                    title: extractLocalizedString(menuSection.title, locale),
                    isHidden: !sectionConditionResult.result,
                };
            }),
            locationId: menu.locationId,
            image: imageItem ? extractImageUrl(imageItem) : undefined,
            isHidden: !conditionResult.result,
        };
    });
}
export function getDisplayablePrice(price, locale, currency) {
    if (price === void 0) { price = 0; }
    var formattedLocale = locale.replace('_', '-');
    var numberFormat = new Intl.NumberFormat(formattedLocale, {
        style: 'currency',
        currency: currency,
    });
    return numberFormat.format(price / 100);
}
function isSoldOut(item, dispatchTime) {
    var _a;
    var reasons = evaluateCondition(item.condition, { dispatchTime: dispatchTime }).reasons;
    var isExplicitlySoldOut = !!reasons.find(function (reason) {
        return reason.type === 'order_delivery_time' &&
            reason.reason === UnavailabilityReason.SoldOut;
    });
    var isOutOfStock = ((_a = item.stock) === null || _a === void 0 ? void 0 : _a.count) === 0;
    return isExplicitlySoldOut || isOutOfStock;
}
/*
When a dish has an option with several choices that are also dishes on the menu (e.g. a "pizza+drink" combo where the pizzas and drinks are also available on their own):
Any choice that is currently out of stock, should appear on the option list as disabled.
If all choice of an option are out of stock and the option is mandatory - r.g. you have to select a drink but all drinks are sold out, we should show the entire dish (e.g. "pizza+drink") as out of stock, with all options within as disabled.
Same should apply if the "added" dish is hidden or otherwise unavailable.
*/
export function hasVariationSelectableChoices(_a) {
    var menu = _a.menu, variation = _a.variation, dispatchTime = _a.dispatchTime, platform = _a.platform, dispatchType = _a.dispatchType, itemsHash = _a.itemsHash;
    var choicesWithoutErrors = variation.itemIds.filter(function (itemId) {
        var item = itemsHash[itemId];
        var _a = getErrorsOfChoice({
            item: item,
            dispatchTime: dispatchTime,
            platform: platform,
            dispatchType: dispatchType,
            menu: menu,
            itemsHash: itemsHash,
        }), errors = _a.errors, isDishDeleted = _a.isDishDeleted;
        return errors.length === 0 && !isDishDeleted;
    });
    var min = variation.minNumAllowed || 0;
    var hasEnoughSelectableChoices = min <= choicesWithoutErrors.length;
    var reasons;
    if (!hasEnoughSelectableChoices) {
        var errors = variation.itemIds
            .map(function (itemId) {
            var item = itemsHash[itemId];
            var _a = getErrorsOfChoice({
                item: item,
                dispatchTime: dispatchTime,
                platform: platform,
                dispatchType: dispatchType,
                menu: menu,
                itemsHash: itemsHash,
            }), choiceErrors = _a.errors, isDishDeleted = _a.isDishDeleted;
            if (choiceErrors.length === 0 && !isDishDeleted) {
                return;
            }
            if (choiceErrors.find(function (err) {
                return err.type === 'order_delivery_time' && err.reason === 'soldout';
            })) {
                return UnavailabileVariationReason.SoldOut;
            }
            else if (choiceErrors.find(function (err) { return err.type === 'order_platform'; })) {
                return UnavailabileVariationReason.UnavailablePlatform;
            }
            else if (choiceErrors.find(function (err) { return err.type === 'order_delivery_type'; })) {
                return UnavailabileVariationReason.UnavailableDeliveryMethod;
            }
            else if (isDishDeleted ||
                choiceErrors.find(function (err) { return err.type === 'false'; })) {
                return UnavailabileVariationReason.Unavailable;
            }
            else {
                return UnavailabileVariationReason.UnavailableTime;
            }
        })
            .filter(function (err) { return err; });
        reasons = errors.find(function (err) { return err === UnavailabileVariationReason.SoldOut; })
            ? [UnavailabileVariationReason.SoldOut]
            : errors;
    }
    return { hasEnoughSelectableChoices: hasEnoughSelectableChoices, reasons: reasons };
}
//# sourceMappingURL=menuLogic.js.map